import React, { useState } from 'react';
import { Box, CircularProgress, Dialog, IconButton, Typography } from '@mui/material';
import AttachFileOutlinedIcon from '@mui/icons-material/AttachFileOutlined';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import AudioFileOutlinedIcon from '@mui/icons-material/AudioFileOutlined';
import LinkOutlinedIcon from '@mui/icons-material/LinkOutlined';
import OpenInNewOutlinedIcon from '@mui/icons-material/OpenInNewOutlined';
import CloseIcon from '@mui/icons-material/Close';
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined';
import { VideoModalPlayer } from './VideoModalPlayer';
import { FONT_ENUM } from '../../../types/global/fonts.types';
import type { IMessageAttachment } from '../../../redux/buzzbridge/campaignWorkspace.types';
import { attachmentOpenUrl, attachmentPlaybackUrl } from '../../../utils/campaignThreadAttachment';
import { BRAND_LUXE_PRIMARY } from '../brandDashboardTokens';

const CREATOR_GOLD = '#ffc965';
const CREATOR_ON_GOLD = '#392700';

export type CampaignThreadAttachmentVariant = 'brand' | 'creator';

type ImagePreviewState = {
    src: string;
    alt: string;
    downloadUrl: string;
    filename: string;
};

function imageDownloadUrl(att: IMessageAttachment, previewSrc: string): string {
    return attachmentOpenUrl(att) || att.url || previewSrc;
}

async function downloadImageAttachment(url: string, filename: string): Promise<void> {
    try {
        const response = await fetch(url);
        const blob = await response.blob();
        const objectUrl = URL.createObjectURL(blob);
        const anchor = document.createElement('a');
        anchor.href = objectUrl;
        anchor.download = filename;
        document.body.appendChild(anchor);
        anchor.click();
        anchor.remove();
        URL.revokeObjectURL(objectUrl);
    } catch {
        const anchor = document.createElement('a');
        anchor.href = url;
        anchor.download = filename;
        anchor.target = '_blank';
        anchor.rel = 'noopener noreferrer';
        document.body.appendChild(anchor);
        anchor.click();
        anchor.remove();
    }
}

function attachmentLabel(att: IMessageAttachment): string {
    if (att.filename?.trim()) return att.filename.trim();
    switch (att.kind) {
        case 'video':
            return 'Video';
        case 'audio':
            return 'Audio';
        case 'image':
            return 'Image';
        case 'link':
            return 'Thspian link';
        default:
            return 'Attachment';
    }
}

function ProcessingAttachment({ att, accentColor }: { att: IMessageAttachment; accentColor: string }) {
    const progress = att.processing_progress;
    return (
        <Box
            sx={{
                mt: 1.5,
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                px: 1.5,
                py: 1.25,
                borderRadius: 1.5,
                bgcolor: 'rgba(255,255,255,0.04)',
                border: '1px solid rgba(255,255,255,0.06)',
            }}>
            <CircularProgress
                size={22}
                variant={typeof progress === 'number' ? 'determinate' : 'indeterminate'}
                value={progress ?? 0}
                sx={{ color: accentColor }}
            />
            <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ fontFamily: FONT_ENUM.MAIN_BOLD, fontSize: 12, color: accentColor }}>
                    Processing {attachmentLabel(att).toLowerCase()}
                    {typeof progress === 'number' ? ` · ${Math.round(progress)}%` : ''}
                </Typography>
                {att.processing_error ? (
                    <Typography sx={{ fontSize: 11, color: '#f87171', mt: 0.25 }}>{att.processing_error}</Typography>
                ) : null}
            </Box>
        </Box>
    );
}

export function CampaignThreadAttachmentGrid({
    attachments,
    isMine,
    variant,
}: {
    attachments: IMessageAttachment[];
    isMine: boolean;
    variant: CampaignThreadAttachmentVariant;
}) {
    const isCreator = variant === 'creator';
    const accentColor = isCreator ? (isMine ? CREATOR_ON_GOLD : CREATOR_GOLD) : BRAND_LUXE_PRIMARY;
    const [videoModalUrl, setVideoModalUrl] = useState<string | null>(null);
    const [imagePreview, setImagePreview] = useState<ImagePreviewState | null>(null);
    const [downloadingImage, setDownloadingImage] = useState(false);

    const processing = attachments.filter(a => a.processing_complete === false && a.kind !== 'location');
    const ready = attachments.filter(a => a.processing_complete !== false && a.kind !== 'location');
    const imageItems = ready.filter(a => a.kind === 'image');
    const nonImages = ready.filter(a => a.kind !== 'image');

    const imageUrls = imageItems.map(att => attachmentPlaybackUrl(att)).filter(Boolean);

    return (
        <>
            {imageUrls.length > 0 ? (
                <Box
                    sx={{
                        mt: 1.5,
                        display: 'grid',
                        gridTemplateColumns: imageUrls.length > 1 ? '1fr 1fr' : '1fr',
                        gap: 1,
                    }}>
                    {imageItems.map(att => {
                        const src = attachmentPlaybackUrl(att);
                        if (!src) return null;
                        const filename = att.filename?.trim() || attachmentLabel(att);
                        return (
                            <Box
                                key={att.id ?? src}
                                onClick={() =>
                                    setImagePreview({
                                        src,
                                        alt: filename,
                                        downloadUrl: imageDownloadUrl(att, src),
                                        filename,
                                    })
                                }
                                sx={{
                                    display: 'block',
                                    borderRadius: 1,
                                    overflow: 'hidden',
                                    cursor: 'pointer',
                                    '&:hover img': { opacity: 0.92 },
                                }}>
                                <Box
                                    component="img"
                                    src={src}
                                    alt={att.filename ?? ''}
                                    sx={{
                                        width: '100%',
                                        height: isCreator ? 96 : 140,
                                        objectFit: 'cover',
                                        display: 'block',
                                        border: '1px solid rgba(255,255,255,0.06)',
                                        borderRadius: 1,
                                        transition: 'opacity 0.15s',
                                    }}
                                />
                            </Box>
                        );
                    })}
                </Box>
            ) : null}

            {nonImages.map(att => {
                const key = att.id ?? att.url ?? att.watch_url ?? attachmentLabel(att);
                if (!att.processing_complete) return null;

                if (att.kind === 'link') {
                    const href = attachmentOpenUrl(att);
                    return (
                        <Box
                            key={key}
                            component="a"
                            href={href}
                            target="_blank"
                            rel="noopener noreferrer"
                            sx={{
                                mt: 1.5,
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                                px: 1.5,
                                py: 1,
                                borderRadius: 1.5,
                                bgcolor: 'rgba(255,255,255,0.04)',
                                textDecoration: 'none',
                                '&:hover': { bgcolor: 'rgba(255,255,255,0.08)' },
                            }}>
                            <LinkOutlinedIcon sx={{ fontSize: 16, color: accentColor }} />
                            <Typography
                                sx={{
                                    fontFamily: FONT_ENUM.MAIN_BOLD,
                                    fontSize: 12,
                                    color: accentColor,
                                    flex: 1,
                                    wordBreak: 'break-all',
                                }}>
                                {att.filename ?? href}
                            </Typography>
                            <OpenInNewOutlinedIcon sx={{ fontSize: 14, color: accentColor, opacity: 0.7 }} />
                        </Box>
                    );
                }

                if (att.kind === 'audio') {
                    const src = attachmentPlaybackUrl(att);
                    return (
                        <Box key={key} sx={{ mt: 1.5 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                                <AudioFileOutlinedIcon sx={{ fontSize: 16, color: accentColor }} />
                                <Typography sx={{ fontFamily: FONT_ENUM.MAIN_BOLD, fontSize: 11, color: accentColor }}>
                                    {attachmentLabel(att)}
                                </Typography>
                            </Box>
                            {src ? (
                                <Box
                                    component="audio"
                                    controls
                                    src={src}
                                    sx={{ width: '100%', height: 32, outline: 'none', borderRadius: 1 }}
                                />
                            ) : null}
                        </Box>
                    );
                }

                if (att.kind === 'video') {
                    const preview = attachmentPlaybackUrl(att);
                    const playUrl = att.hls ?? att.url ?? preview;
                    return (
                        <Box
                            key={key}
                            onClick={() => playUrl && setVideoModalUrl(playUrl)}
                            sx={{
                                mt: 1.5,
                                borderRadius: 1.5,
                                overflow: 'hidden',
                                bgcolor: '#000',
                                position: 'relative',
                                cursor: playUrl ? 'pointer' : 'default',
                            }}>
                            {preview ? (
                                <Box
                                    component="video"
                                    src={preview}
                                    poster={att.thumbnail_url}
                                    playsInline
                                    sx={{
                                        width: '100%',
                                        maxHeight: isCreator ? 200 : 260,
                                        display: 'block',
                                        outline: 'none',
                                        pointerEvents: 'none',
                                    }}
                                />
                            ) : (
                                <Box sx={{ px: 2, py: 3, textAlign: 'center' }}>
                                    <Typography sx={{ fontSize: 12, color: 'rgba(255,255,255,0.7)' }}>
                                        {att.watch_url ? 'Video processing…' : 'Video'}
                                    </Typography>
                                </Box>
                            )}
                            {playUrl ? (
                                <Box
                                    sx={{
                                        position: 'absolute',
                                        inset: 0,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        bgcolor: 'rgba(0,0,0,0.35)',
                                    }}>
                                    <PlayCircleOutlineIcon sx={{ fontSize: 48, color: '#fff' }} />
                                </Box>
                            ) : null}
                            {att.watch_url ? (
                                <Box
                                    component="a"
                                    href={att.watch_url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    onClick={e => e.stopPropagation()}
                                    sx={{
                                        position: 'absolute',
                                        top: 8,
                                        right: 8,
                                        px: 1,
                                        py: 0.25,
                                        borderRadius: 1,
                                        bgcolor: 'rgba(0,0,0,0.55)',
                                        fontSize: 10,
                                        color: '#fff',
                                        textDecoration: 'none',
                                    }}>
                                    Open
                                </Box>
                            ) : null}
                        </Box>
                    );
                }

                const href = attachmentOpenUrl(att);
                return (
                    <Box
                        key={key}
                        component="a"
                        href={href || undefined}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{
                            mt: 1.5,
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            bgcolor: 'rgba(255,255,255,0.04)',
                            px: 1.5,
                            py: 1,
                            borderRadius: 1.5,
                            cursor: href ? 'pointer' : 'default',
                            textDecoration: 'none',
                            '&:hover': href ? { bgcolor: 'rgba(255,255,255,0.08)' } : undefined,
                        }}>
                        <AttachFileOutlinedIcon sx={{ fontSize: 16, color: accentColor }} />
                        <Typography sx={{ fontFamily: FONT_ENUM.MAIN_BOLD, fontSize: 12, color: accentColor }}>
                            {attachmentLabel(att)}
                        </Typography>
                    </Box>
                );
            })}

            {processing.map(att => (
                <ProcessingAttachment key={att.id ?? att.url} att={att} accentColor={accentColor} />
            ))}

            <Dialog
                open={Boolean(videoModalUrl)}
                onClose={() => setVideoModalUrl(null)}
                maxWidth="md"
                fullWidth
                PaperProps={{
                    sx: { bgcolor: '#000', p: 0, borderRadius: 2, overflow: 'hidden' },
                }}>
                {videoModalUrl ? <VideoModalPlayer src={videoModalUrl} onClose={() => setVideoModalUrl(null)} /> : null}
            </Dialog>

            <Dialog
                open={Boolean(imagePreview)}
                onClose={() => {
                    setImagePreview(null);
                    setDownloadingImage(false);
                }}
                maxWidth="lg"
                fullWidth
                PaperProps={{
                    sx: { bgcolor: '#000', p: 0, borderRadius: 2, overflow: 'hidden' },
                }}>
                {imagePreview ? (
                    <Box sx={{ position: 'relative', bgcolor: '#000' }}>
                        <IconButton
                            onClick={() => setImagePreview(null)}
                            aria-label="Close image preview"
                            sx={{
                                position: 'absolute',
                                top: 8,
                                right: 8,
                                zIndex: 2,
                                bgcolor: 'rgba(0,0,0,0.55)',
                                color: '#fff',
                                '&:hover': { bgcolor: 'rgba(0,0,0,0.75)' },
                            }}>
                            <CloseIcon />
                        </IconButton>
                        <Box
                            role="button"
                            tabIndex={0}
                            aria-label="Download image"
                            onClick={async e => {
                                e.stopPropagation();
                                if (downloadingImage) return;
                                setDownloadingImage(true);
                                try {
                                    await downloadImageAttachment(imagePreview.downloadUrl, imagePreview.filename);
                                } finally {
                                    setDownloadingImage(false);
                                }
                            }}
                            onKeyDown={e => {
                                if (e.key !== 'Enter' && e.key !== ' ') return;
                                e.preventDefault();
                                e.currentTarget.click();
                            }}
                            sx={{
                                position: 'absolute',
                                top: 8,
                                left: 8,
                                zIndex: 2,
                                display: 'flex',
                                alignItems: 'center',
                                gap: 0.5,
                                px: 1.25,
                                py: 0.5,
                                borderRadius: 1,
                                bgcolor: 'rgba(0,0,0,0.55)',
                                color: '#fff',
                                fontSize: 12,
                                fontFamily: FONT_ENUM.MAIN_BOLD,
                                cursor: downloadingImage ? 'wait' : 'pointer',
                                opacity: downloadingImage ? 0.7 : 1,
                                '&:hover': { bgcolor: 'rgba(0,0,0,0.75)' },
                            }}>
                            {downloadingImage ? (
                                <CircularProgress size={14} sx={{ color: '#fff' }} />
                            ) : (
                                <DownloadOutlinedIcon sx={{ fontSize: 16 }} />
                            )}
                            Download
                        </Box>
                        <Box
                            component="img"
                            src={imagePreview.src}
                            alt={imagePreview.alt}
                            sx={{
                                width: '100%',
                                maxHeight: '85vh',
                                objectFit: 'contain',
                                display: 'block',
                            }}
                        />
                    </Box>
                ) : null}
            </Dialog>
        </>
    );
}
